// Inline notice for VDB-backed surfaces (entity detail, search, viz, vocab).
// The knowledge index updates after every data load. Usually the previous
// index keeps answering while it does (a merge, or a full build before it
// clears the store) and the only effect is that the newest data is not in
// search yet; in a rebuild's index phase there is nothing to answer from and
// searches return nothing. Regular users have no way to connect either to the
// Welcome page's VDB badge — this surfaces the state where it is felt, and
// says which of the two it is. Renders nothing once the index is ready.

import { deferredDetail, useVdbStatus } from '@/lib/useVdb';
import { cn } from '@/lib/utils';

export function VdbBuildingNotice({ className }: { className?: string }) {
  const status = useVdbStatus();
  const state = status.data?.state;

  if (status.data?.state === 'building' && status.data.serving) {
    return (
      <div
        className={cn(
          'rounded-md border border-tres-info/50 bg-tres-info/10 px-3 py-2 text-xs text-tres-text-primary',
          className,
        )}
      >
        The knowledge index is updating after a data load. Search and entity details keep
        answering from the previous index; the newest data appears when the update finishes —
        this page updates automatically.
      </div>
    );
  }

  if (state === 'building' || state === 'pending') {
    return (
      <div
        className={cn(
          'rounded-md border border-tres-warning/50 bg-tres-warning/10 px-3 py-2 text-xs text-tres-text-primary',
          className,
        )}
      >
        The knowledge index is rebuilding after a data load. Search results and entity
        details may be empty or slow until it finishes — this page updates automatically
        when it is ready.
      </div>
    );
  }

  if (status.data?.state === 'deferred') {
    return (
      <div
        className={cn(
          'rounded-md border border-tres-error/50 bg-tres-error/10 px-3 py-2 text-xs text-tres-text-primary',
          className,
        )}
      >
        The knowledge index could not be rebuilt after the last data change —{' '}
        {deferredDetail(status.data)}. The previous index keeps serving; ask your administrator.
      </div>
    );
  }

  if (state === 'failed') {
    return (
      <div
        className={cn(
          'rounded-md border border-tres-error/50 bg-tres-error/10 px-3 py-2 text-xs text-tres-text-primary',
          className,
        )}
      >
        The knowledge index failed to build — search and entity details may be incomplete.
        Ask your administrator to reload the data.
      </div>
    );
  }

  return null;
}
